import _ from 'lodash';
import customSearchResultsTemplate from '../../../views/templates/search/customSearchResults';
import MessageBusTopics from '../../../../common/MessageBusTopics';
import recentSearchesTemplate from '../../../views/templates/search/recentSearches';
import searchResultsTemplate from '../../../views/templates/search/searchResults';
import sportSearchResultsTemplate from '../../../views/templates/search/sportSearchResults';

/**
 * Search UI DOM Updater constructor
 *
 * @param {Object} messageBus  WH message bus
 * @param {Object} dom  DOM objects
 *
 * @constructor
 */
function SearchUiDomUpdater(dom, deps) {

    this.deps = deps || {};

    this.dom = dom;
    this.scrollTop = 0;
    this.hasMoreResults = false;

    this.isInitialSearchInputState = true;
    this.hasRecentSearches = false;

    this.translations = {
        searchHelp: 'components.misc.search.searchHelp',
        searchNotFound: 'components.misc.search.searchNotFound',
        searchFound: 'components.misc.search.searchFound',
        searchFoundSingular: 'components.misc.search.searchFoundSingular',
    };

    this.subscribeToEvents();
}

/**
 * Subscribe for search UI related events
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.subscribeToEvents = function () {

    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_OPEN, this.onOpen.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_CLOSE, this.onClose.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_INPUT_CHANGE, this.onInputChange.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_STATE_RESET, this.onStateReset.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_REQUEST_START, this.onRequestStart.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_REQUEST_START_LOAD_MORE, this.onLoadMoreRequestStart.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_REQUEST_SUCCESS, this.onRequestSuccess.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_REQUEST_FAILED, this.onRequestFail.bind(this)
    );

    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_RECENT_SEARCHES_UPDATE, this.onRecentSearchesUpdate.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_EVENT_ITEMS_UPDATE, this.onEventItemsUpdate.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_ICON_BAR_UPDATE, this.onIconBarItemsUpdate.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_LABELS_UPDATE, this.onLabelsUpdate.bind(this)
    );

    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_RECENT_SEARCHES_ITEM_CLICK, this.onRecentSearchesItemClick.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_EVENT_RESULT_ITEM_CLICK, this.onEventResultItemClick.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_ICON_BAR_ITEM_CLICK, this.onIconBarItemClick.bind(this)
    );

    window.WH.messageBus.subscribe(
        MessageBusTopics.SEARCH_WINDOW_RESIZE, this.onWindowResize.bind(this)
    );

    window.WH.messageBus.subscribe(
        MessageBusTopics.NAVIGATION_QUERY_STRING_SEARCH, this.onQueryString.bind(this)
    );

    window.WH.messageBus.subscribe(
        MessageBusTopics.ADD_BET, this.showAddToSlipSuccess.bind(this)
    );
    window.WH.messageBus.subscribe(
        MessageBusTopics.REMOVE_BET, this.showRemoveFromSlipSuccess.bind(this)
    );
};

// event handlers

/**
 * Open Search event handler
 *
 * Prepares the page to display the search overview
 *
 * @param {String} eventName  Event name
 * @param {Object} clickEvent  DOM event object
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onOpen = function () {
    this.scrollTop = this.dom.body.getScrollPosition();
    this.dom.overlay.show();
    this.dom.mainOffCanvas.fix();
    this.showRecentSearches();
    this.addQueryString();
    this.focusOnInput();
};

/**
 * Close Search event handler
 *
 * Removes the search overview
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onClose = function () {
    // scroll the body on the position it was before opening
    this.dom.body.setScrollPosition(this.scrollTop);

    // hiding search overlay
    this.dom.mainOffCanvas.unfix();
    this.dom.overlay.hide();

    // clear search input value
    this.dom.input.clearValue();

    this.setInitialInputState();
    this.resetHeightOnNeed();
    this.removeQueryString();
};

/**
 * Search Input change event handler
 *
 * Reflects the new state on the UI.
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onInputChange = function () {
    this.dom.notFoundMessage.hide();
    this.dom.error.hide();
    this.removeInitialInputState();
};

/**
 * Search State reset event handler
 *
 * Reset the UI to the initial state
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onStateReset = function () {
    this.clearResults();
};

/**
 * Search Request start event handler
 *
 * Prepares the UI to indicate that new search results about to come.
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onRequestStart = function () {
    this.clearResults();
    this.dom.progress.show();
    this.resetHeightOnNeed();
};

/**
 * Load More Request start event handler
 *
 * Displays the progress and forcing to show scroll bar if more results are available.
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onLoadMoreRequestStart = function () {
    this.dom.progress.show();
    this.resetHeightOnNeed();
};

/**
 * Search Request success event handler
 *
 * Prepares the UI for display the search results.
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onRequestSuccess = function () {
    this.dom.progress.hide();
};

/**
 *  Search Request fail event handler
 *
 *  It displays error message if the request wasn't aborted
 *
 * @param {String} eventName
 * @param {Object} error
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onRequestFail = function (eventName, error) {

    if (error.statusText !== 'abort') {
        this.dom.progress.hide();
        this.dom.error.show();
        this.dom.recentSearchesContainer.hide();
    }
};

/**
 * Recent Searches update event handler
 *
 * Updates the items the Recent Searches container
 *
 * @param {String} eventName
 * @param {Array} recentSearches
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onRecentSearchesUpdate = function (eventName, recentSearches) {

    var htmlCode = '',
        recentItems = recentSearches || [],
        Translator = this.deps.Translator;

    recentItems.forEach(function (recentSearch) {

        htmlCode += _.template(recentSearchesTemplate)({ data: recentSearch, translator: Translator });
    });

    if (htmlCode !== '') {
        this.hasRecentSearches = true;
        this.dom.recentSearchesResults.setContent(htmlCode);
    } else {
        this.hasRecentSearches = false;
    }

    this.deps.OddsFormatProvider.updateFragmentOdds(this.dom.eventsResults.selector);
};

/**
 * Updating events items in the search results
 *
 * @param {String} eventName
 * @param {Object} update
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onEventItemsUpdate = function (eventName, update) {

    var htmlCode = '',
        Translator = this.deps.Translator;

    update.results.items.forEach(function (event) {

        htmlCode += _.template(searchResultsTemplate)({ data: event, translator: Translator });
    });

    if (htmlCode !== '') {

        if (update.reset) {

            this.dom.eventsResults.setContent(htmlCode);
            this.dom.eventsContainer.show();
        } else {

            this.dom.eventsResults.appendContent(htmlCode);
        }

        this.hasMoreResults = update.hasMoreResults;
        this.displayScrollMaskOnNeed(this.hasMoreResults);
        this.extendHeightOnNeed(update.hasMoreResults);
        window.WH.messageBus.publish(MessageBusTopics.SEARCH_EVENT_ITEMS_UPDATED);
    }

    this.deps.OddsFormatProvider.updateFragmentOdds(this.dom.eventsResults.selector);
};

/**
 * Updating sports items in the search results
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onIconBarItemsUpdate = function (eventName, update) {
    var htmlCode = '',
        Translator = this.deps.Translator;

    if (update.reset) {

        update.customResults.items.forEach(function (event) {

            htmlCode += _.template(customSearchResultsTemplate)({ data: event, translator: Translator });
        });

        update.sportsResults.items.forEach(function (event) {

            htmlCode += _.template(sportSearchResultsTemplate)({ data: event, translator: Translator });
        });

        if (htmlCode !== '') {

            this.dom.quickLinksContainer.show();
            this.dom.quickLinksResults.setContent(htmlCode);
        }
    }
};

/**
 * Callback function on the event when response received from the backend
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onLabelsUpdate = function (eventName, update) {

    var quickLinksCount = update.sportsTotal + update.customTotal;

    var allTheResults = update.eventsTotal + quickLinksCount;

    if (allTheResults === 0) {

        this.dom.notFoundMessage.show();
    } else {

        this.dom.recentSearchesContainer.hide();
        this.dom.quickLinksCount.setContent(quickLinksCount);
        this.dom.eventsCount.setContent(update.eventsTotal);
    }

};

/**
 * Recent Search Item click event handler
 *
 * Should trigger a search with the clicked term.
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onRecentSearchesItemClick = function (eventName, item) {
    this.dom.input.setValue(item.target.innerText);
    this.dom.input.triggerChange();
};

/**
 * Events Results Item click event handler
 *
 * Navigate to the Event page and removes the search overlay
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onEventResultItemClick = function () {
    this.clickResults();
};

/**
 * Icon Bar Item click event handler
 *
 * Navigate to the Event page and removes the search overlay
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onIconBarItemClick = function () {
    this.clickResults();
};

/**
 * Window resize event handler
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.onWindowResize = function () {
    if (this.dom.isSmallViewPort()) {
        this.extendHeightOnNeed(this.hasMoreResults);
    } else {
        this.dom.innerOverlay.resetHeight();
    }
};

/**
 * Navigation Query String (window.location.search) received a search request
 * @param event
 * @param {object} params - should contain the query string in key-value pairs
 */
SearchUiDomUpdater.prototype.onQueryString = function (event, params) {
    // fill the input and send search
    this.dom.input.setValue(params.query);
    this.dom.input.triggerChange();
    window.WH.messageBus.publish(MessageBusTopics.SEARCH_OPEN);
};

// utility functions

/**
 * Clicking on sport or event result item
 *
 * Reset the state and removes the overlay
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.clickResults = function () {

    this.clearResults();
    this.onClose();
};

/**
 * Removing initial state of the serach input
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.removeInitialInputState = function () {

    if (this.isInitialSearchInputState) {

        this.dom.overlay.removeInitialState();
    }

    this.isInitialSearchInputState = false;
};

/**
 * Set initial state of the serach input
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.setInitialInputState = function () {

    this.dom.overlay.restoreInitialState();
    this.isInitialSearchInputState = true;
};

/**
 * Resets the results to default (empty)
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.clearResults = function () {
    this.dom.eventsContainer.hide();
    this.dom.quickLinksContainer.hide();
    this.dom.progress.hide();
    this.dom.error.hide();
    this.dom.notFoundMessage.hide();
    this.dom.eventsResults.hideScrollMask();
    this.dom.addedToSlipSuccessMessage.hide();
    this.dom.removedFromSlipSuccessMessage.hide();
    this.resetHeightOnNeed();
    this.showRecentSearches();
};

/**
 * Shows or hides the Scroll Mask
 *
 * @param {Boolean} showScrollMask
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.displayScrollMaskOnNeed = function (showScrollMask) {
    if (showScrollMask === true) {
        this.dom.eventsResults.showScrollMask();
    } else {
        this.dom.eventsResults.hideScrollMask();
    }
};

/**
 * Shrinks height of layout for enables scroll on small viewports.
 *
 * @param {Boolean} extendHeight
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.extendHeightOnNeed = function (extendHeight) {
    if (this.dom.isSmallViewPort() && extendHeight === true) {
        this.dom.innerOverlay.extendHeightWith(10);
    }
};

/**
 * Resets height of events results list on small viewports
 *
 * This disables scroll.
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.resetHeightOnNeed = function () {
    if (this.dom.isSmallViewPort()) {
        this.dom.innerOverlay.resetHeight();
    }
};

/**
 * Show recent searches when there are items to show
 */
SearchUiDomUpdater.prototype.showRecentSearches = function () {
    if (this.hasRecentSearches) {
        this.removeInitialInputState();
        this.dom.recentSearchesContainer.show();
    }
};

/**
 * Display the successfully added to slip message
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.showAddToSlipSuccess = function () {
    this.dom.removedFromSlipSuccessMessage.hide();
    this.dom.addedToSlipSuccessMessage.hide();
    this.dom.addedToSlipSuccessMessage.redraw(); // force redraw so element can be updated correctly
    this.dom.addedToSlipSuccessMessage.show();
};

/**
 * Display the successfully removed from slip message
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomUpdater.prototype.showRemoveFromSlipSuccess = function () {
    this.dom.addedToSlipSuccessMessage.hide();
    this.dom.removedFromSlipSuccessMessage.hide();
    this.dom.removedFromSlipSuccessMessage.redraw(); // force redraw so element can be updated correctly
    this.dom.removedFromSlipSuccessMessage.show();
};

SearchUiDomUpdater.prototype.updateHash = function (newHash) {
    var { history, location } = window;
    if (history && history.hasOwnProperty('pushState')) {
        history.pushState('', document.title, `${location.pathname}${location.search}${newHash ? `#${newHash}`: ''}`);
    } else {
        location.hash = newHash;
    }
};

/**
 * Adds the "#search" string in the url
 */
SearchUiDomUpdater.prototype.addQueryString = function() {
    this.updateHash('search');
    setTimeout(() => {
        window.addEventListener('popstate', this.publishClose);
    }, 100);
};

SearchUiDomUpdater.prototype.focusOnInput = function() {
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
        let inpt = document.querySelector('#search-input');
        if (inpt) {
            inpt.click();
            inpt.focus();
        }
    }, 300);
};

/**
 * Publishes the close event on messageBus
 * @param {PopStateEvent}  event    Event fired by a browser forward or back event
 */
SearchUiDomUpdater.prototype.publishClose = function (event) {
    if (window.WH && window.WH.messageBus && window.WH.messageBus.publish) {
        window.WH.messageBus.publish(MessageBusTopics.SEARCH_CLOSE, event);
    }
};

/**
 * Removes the "#search" string from the url if the search closed.
 */
SearchUiDomUpdater.prototype.removeQueryString = function() {
    if (window.location.hash === '#search') {
        this.updateHash('');
    }
    window.removeEventListener('popstate', this.publishClose);
};

export default SearchUiDomUpdater;
